import type { ShellNavSection } from "@/components/shell/types";
import type { TodoKey } from "@/features/admin-todo/todo-counts";
import { fieldCollectionCopyFor } from "@/features/admin-field-collection/field-collection.copy";
import type { useApp } from "@/hooks/use-app-context";

type Translations = ReturnType<typeof useApp>["t"];

/** Counters shown on the menu entries where that work gets done. */
export type AdminNavBadges = Partial<Record<TodoKey, number>>;

/**
 * The admin menu: seven groups, one level, everything an admin needs and
 * nothing else up front. Technical pages sit in "Advanced" at the bottom.
 */
export function buildAdminNav(
  lang: string,
  t: Translations,
  role: string | undefined,
  badges: AdminNavBadges = {},
): ShellNavSection[] {
  const en = lang === "en";
  const nav = t.admin.layout.nav;
  const base = `/${lang}/admin`;
  const isSuperAdmin = role === "super_admin";

  return [
    {
      key: "home",
      items: [{ key: "dashboard", label: nav.dashboard, href: `${base}/dashboard`, icon: "layoutDashboard" }],
    },
    {
      key: "stats",
      title: en ? "Statistics" : "Statistiques",
      // One page, three tabs (collection, by type, indicators): the entry
      // stays lit on every tab, and the old addresses redirect there.
      items: [
        {
          key: "stats",
          label: en ? "Statistics" : "Statistiques",
          href: `${base}/stats`,
          icon: "barChart3",
          match: [`${base}/collection`, `${base}/metrics`, `${base}/contributions/stats`],
          keywords: ["analytics", en ? "collection" : "collecte", en ? "indicators" : "indicateurs"],
        },
      ],
    },
    {
      key: "contributions",
      title: "Contributions",
      items: [
        { key: "audio", label: en ? "Audio" : "Audios", href: `${base}/contributions/audio`, icon: "volume2" },
        { key: "text", label: en ? "Texts" : "Textes", href: `${base}/contributions/text`, icon: "fileText" },
        { key: "transcriptions", label: "Transcriptions", href: `${base}/contributions/transcriptions`, icon: "mic" },
        { key: "images", label: "Images", href: `${base}/contributions/images`, icon: "image" },
        { key: "initial-data", label: en ? "Answers" : "Réponses", href: `${base}/contributions/initial-data`, icon: "messageSquare", keywords: ["initial data", "données initiales"] },
        // Read-only tracking of the collectors a super admin opened to this admin.
        { key: "field-collection", label: fieldCollectionCopyFor(lang).navLabel, href: `${base}/field-collection`, icon: "mapPin", keywords: [en ? "collectors" : "collecteurs", en ? "community profiles" : "profils communautaires"] },
      ],
    },
    {
      key: "moderation",
      title: en ? "Moderation" : "Modération",
      items: [
        { key: "tests", label: en ? "Qualification tests" : "Tests de qualification", href: `${base}/test-moderation`, icon: "checkCircle", keywords: [t.getPaid.testCorrection], badge: badges.tests },
        { key: "translations", label: nav.excelTranslations, href: `${base}/excel-translations`, icon: "languages", badge: badges.translations },
        { key: "complaints", label: nav.complaints, href: `${base}/complaints`, icon: "flag", badge: badges.complaints },
      ],
    },
    {
      key: "content",
      title: en ? "Content" : "Contenus",
      items: [
        { key: "uploads", label: nav.uploads, href: `${base}/uploads/audio`, icon: "upload", match: [`${base}/uploads`] },
        { key: "themes", label: en ? "Themes and phrases" : "Thèmes et phrases", href: `${base}/uploads-excels`, icon: "bookOpen", match: [`${base}/config/theme`], badge: badges.phraseFiles },
        { key: "languages", label: nav.languages, href: `${base}/config/languages`, icon: "globe" },
        { key: "faq", label: "FAQ", href: `${base}/faq`, icon: "info" },
        { key: "guidelines", label: nav.recordingGuidelines, href: `${base}/recording-guidelines`, icon: "shield" },
      ],
    },
    {
      key: "team",
      title: en ? "Team" : "Équipe",
      items: [
        { key: "users", label: nav.users, href: `${base}/users`, icon: "users" },
        { key: "campaigns", label: t.admin.panels.campaignsTitle, href: `${base}/campaigns`, icon: "send", keywords: [en ? "reminders" : "rappels", "communication", "SMS", "email"] },
        { key: "contracts", label: nav.contracts, href: `${base}/contracts`, icon: "fileText" },
      ],
    },
    {
      key: "finance",
      title: "Finance",
      items: [{ key: "payments", label: en ? "Payments" : "Paiements", href: `${base}/payments`, icon: "wallet", badge: badges.payments }],
    },
    {
      key: "advanced",
      title: en ? "Advanced" : "Avancé",
      items: [
        { key: "models", label: en ? "AI models" : "Modèles IA", href: `${base}/config/models`, icon: "zap", match: [`${base}/config/api`, `${base}/config/model-performance`] },
        { key: "logs", label: en ? "Technical log" : "Journal technique", href: `${base}/logs`, icon: "history" },
        ...(isSuperAdmin
          ? [
              { key: "api-keys", label: en ? "API keys" : "Clés API", href: `${base}/api-keys`, icon: "lock" as const },
              { key: "mobile-app", label: en ? "Mobile app" : "Application mobile", href: `${base}/config/mobile-app`, icon: "phone" as const },
            ]
          : []),
      ],
    },
  ];
}
